import { Box, Stack } from '@mui/material';
import {
  ArrowCircleRight as ArrowCircleRightIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import {
  StatusEnum,
  stepUtils,
  workUtils,
  useCurrentUserPermissions,
  useWorkPageStore,
} from '@diana/core';
import { StatusIconProps, StatusIconSizeProps } from './Workflow.types';
import { LockIcon } from './LockIcon';

export function Circle({
  children,
  size = 'medium',
}: { children: JSX.Element } & StatusIconSizeProps) {
  const content = (
    <Stack justifyContent="center" alignItems="center" sx={{ p: '3px', width: '24px' }}>
      {children}
    </Stack>
  );

  if (size === 'small') return content;
  return (
    <Box
      sx={{ borderRadius: '50%', background: ({ palette }) => palette.secondary.dark, p: '3px' }}
    >
      {content}
    </Box>
  );
}

export function CircleStatus({
  size,
  disabled = false,
}: { disabled?: boolean } & StatusIconSizeProps) {
  return (
    <Circle size={size}>
      <Box
        sx={{
          width: '18px',
          height: '18px',
          borderRadius: '50%',
          border: ({ palette }) =>
            `1px solid ${disabled ? palette.text.secondary : palette.primary.main}`,
        }}
      />
    </Circle>
  );
}

export function CompletedStatus({ size }: StatusIconSizeProps) {
  return (
    <Circle size={size}>
      <CheckCircleIcon sx={{ fontSize: '22px', color: ({ palette }) => palette.primary.main }} />
    </Circle>
  );
}

export function SkippedStatus({ size }: StatusIconSizeProps) {
  return (
    <Circle size={size}>
      <ArrowCircleRightIcon
        sx={{ fontSize: '22px', color: ({ palette }) => palette.warning.main }}
      />
    </Circle>
  );
}

export function DeletedStatus({ size }: StatusIconSizeProps) {
  return (
    <Circle size={size}>
      <CancelIcon sx={{ fontSize: '22px', color: ({ palette }) => palette.error.main }} />
    </Circle>
  );
}

export function StatusIcon({ step, ...props }: StatusIconProps) {
  const workUsers = useWorkPageStore((s) => s.work?.users);
  const canDelegate = useWorkPageStore((s) => s.work?.canDelegate ?? false);
  const invitedUsers = workUtils.workUsersToInvites(workUsers!);
  const { isFlowOwner, isWorkOwner } = useCurrentUserPermissions({ invitedUsers, canDelegate });
  const { canRead } = stepUtils.checkUserPermissions({ step, work: props.work });
  const showLockIcon = isWorkOwner ? false : !canRead && !isFlowOwner;
  if (showLockIcon) return <LockIcon />;
  switch (step.status) {
    case StatusEnum.NEW:
    case StatusEnum.CREATED:
      return <CircleStatus {...props} disabled />;
    case StatusEnum.PENDING:
    case StatusEnum.IN_PROGRESS:
      return <CircleStatus {...props} />;
    case StatusEnum.SKIPPED:
      return <SkippedStatus {...props} />;
    case StatusEnum.DECLINED:
    case StatusEnum.DELETED:
      return <DeletedStatus {...props} />;
    case StatusEnum.COMPLETE:
      return <CompletedStatus {...props} />;
    default:
      console.warn(`[StatusIcon]: Step status '${step.status}' is unknown.`);
      return <CircleStatus {...props} disabled />;
  }
}
